import { filter, find, isEmpty, map, reject } from '@dt/fe-utils/dist/lodash'
import {
  bankCardMobx,
  memberMobx,
  payChannelMobx,
  payMobx,
  payPlanMobx,
  storeMobx,
  surchargeMobx,
  thirdPartyMobx,
} from '@mobxStore'
import { cartMobx, restaurantConfig } from 'restaurant'
import appMobx from '@mobx/app/app.mobx'
import { store, toDecimal, uuid, getHandTopUrl, getCookie, setCookie } from '@dt/fe-utils'
import { modalLayer, getPlatform } from '@utils'
import { PayApi, OrderAPI } from 'api'
import { GlobalPaymentsModal } from '@cps'
import { BankCardAuthModal } from '@pages/checkout/modal'
import { apConfigG } from './third'

function delay (ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

class CheckoutPayController {
  private static _cardConnectApplePaySession?: ApplePaySession
  private static _globalPaymentsApplePaySession?: ApplePaySession
  private static _stripeApplePayCallback?: (status: any) => void
  private static _asyncPayResult?: (result: any, resultOrdersId?: string) => Promise<any>
  private static _payOpenData?: { openId: string; appId?: string }
  private static _weixinMiniToPay () {
    const { isWeixinMiniProgram } = getPlatform()
    return isWeixinMiniProgram && Config.miniVersion >= 100220816
  }

  /** 点下提交按钮，下单前 */
  static async preparePay (method?: any): Promise<void> {
    payPlanMobx.endCalc()
    try {
      await this._checkOpenId(method)
      await this._prepareBankCardOnline(method)
      await this._prepareApplePayWeb(method)
      await this._prepareAsyncPay(method)
    } catch (e) {
      payPlanMobx.beginCalc()
      throw e
    }
  }

  static async _checkOpenId (methodParam?) {
    const method = ['alipay', 'wechatPay'].includes(methodParam?.key) ? methodParam : find(
      payPlanMobx.payMethods,
      (x) => x.type === 'WechatPayWeb' || x.type === 'AlipayApp'
    )

    console.debug('checkOpenId', method)
    if (method) {
      console.debug('checkOpenId', this._payOpenData)
      if (typeof this._payOpenData?.openId !== 'string' || this._payOpenData?.openId.length < 10) {
        const obj = JSON.parse(memberMobx.thirdAuthInfo) || {}
        const { isWeixinMiniProgram } = getPlatform()
        if (isWeixinMiniProgram) {
          this._payOpenData = {
            openId: window.Config.openId,
            appId: window.Config.appId,
          }
        } else {
          this._payOpenData = {
            openId: obj.gOpenId,
          }
        }
        if (!this._payOpenData.openId) {
          this._payOpenData = await payMobx.fetchGOpenIdAndSave()
        }
      }
    }
  }

  static async _prepareBankCardOnline (methodParam?, payRechargeFlag?: boolean): Promise<void> {
    const bankCardOnlineMethod = methodParam?.key === 'bankCardOnline' ? methodParam : find(
      payPlanMobx.payMethods, (x) => x.type === 'BankCardOnline'
    ) as IBankCardOnlinePayMethod
    // 是否先校验银行卡
    const isPrepare = bankCardOnlineMethod && bankCardOnlineMethod?.channel !== 'GlobalPaymentsBankCard'

    if (isPrepare) {
      const { bankCardInfo } = bankCardOnlineMethod
      if (bankCardInfo.tokenType === 'single' && !bankCardInfo.token) {
        const card = (await modalLayer(
          BankCardAuthModal,
          { payRechargeFlag },
          'bankCardAuthModal'
        )) as ISutBankCardInfo
        if (methodParam) {
          Object.assign(methodParam, {
            bankCardInfo: card
          })
        } else {
          payPlanMobx.setBankCardInfo(card)
        }
      } else if (
        bankCardInfo.tokenType === 'multi' &&
        !bankCardInfo.creditCardNo
      ) {
        const card = (await modalLayer(
          BankCardAuthModal,
          { payRechargeFlag, multiUse: true },
          'bankCardAuthModal'
        )) as IMutBankCardInfo
        if (methodParam) {
          Object.assign(methodParam, {
            bankCardInfo: card
          })
        } else {
          payPlanMobx.setBankCardInfo(card)
        }
      }
    }
  }

  static async _prepareAsyncPay (methodParam?): Promise<void> {
    const externalPayMethod = ['alipay', 'wechatPay'].includes(methodParam?.key) ? methodParam : payPlanMobx.externalMethod
    if (externalPayMethod?.type === 'BankCardOnline' && externalPayMethod.channel === 'GlobalPaymentsBankCard') {
      this._asyncPayResult = async (result) => {
        return new Promise((resolve, reject) => {
          const { serverUrl, hppJson } = result
          modalLayer(GlobalPaymentsModal, { serverUrl, hppJson, hideLoading: true }, 'GlobalPaymentsModal')
            .then(() => {
              resolve(undefined)
            })
            .catch((error) => {
              error ? reject(new Error(error)) : reject()
            })
        })
      }
    } else if (externalPayMethod?.type === 'PayPalWeb') {
      this._asyncPayResult = async (result) => {
        if (result?.token) {
          const { env, envBo } = ENV
          const url = `https://www.${!(env === 'pro' && envBo === 'us') ? 'sandbox.' : ''}paypal.com/checkoutnow?token=${result.token}`
          window.location.href = url
          return 'async'
        }
      }
    } else if (externalPayMethod?.type === 'AlipayApp') {
      this._asyncPayResult = async (result) => {
        if (result.wapPayRequest) {
          const { wapPayRequest } = result
          let tradeNo = wapPayRequest.tradeNo || wapPayRequest.tradeNO
          // if (['ShengPayAlipayWeb', 'ShengPayAlipayWebV2'].includes(externalPayMethod.channel)) {
          //   tradeNo = wapPayRequest.tradeNO
          // }

          if (tradeNo) {
            return new Promise((resolve, reject) => {
              apConfigG.pay(tradeNo, function (resultCode) {
                if (resultCode === '9000') {
                  resolve(undefined)
                } else {
                  reject(new Error(i18n.t('pay.payError.alipayError')))
                }
              })
            })
          } else if (wapPayRequest.code) {
            throw Error(wapPayRequest.subMsg)
          }
        }
      }
    } else if (externalPayMethod?.type === 'WechatPayWeb') {
      this._asyncPayResult = async (result, resultOrdersId) => {
        const { wapPayRequest, ordersId } = result
        const { isWeixinMiniProgram, isWeixinBrower } = getPlatform()
        if (['ShengPayWechatWeb', 'ShengPayWechatWebV2'].includes(externalPayMethod.channel)) {
          wapPayRequest.package = wapPayRequest.packageStr || wapPayRequest.package
        }
        if (isWeixinMiniProgram) {
          let _url = `/pages/pay/index?p=${encodeURIComponent(JSON.stringify(wapPayRequest))}&o=${ordersId}`
          if (resultOrdersId) {
            _url += `&ro=${resultOrdersId}`
          }
          if (cartMobx.posOrderPay) {
            _url += '&scene=posOrderPay'
          }
          setCookie(`externalPayMethod.WechatPayWeb.${restaurantConfig.brandId}`, 1, Date.now() + 15000)
          if (Config.buyTogetherId) {
            setCookie(`buyTogetherId.${restaurantConfig.brandId}`, Config.buyTogetherId, Date.now() + 3 * 60 * 1000)
          }
          wx?.miniProgram?.redirectTo({
            url: _url
          })
          if (Config.buyTogetherId) {
            return 'async'
          }
        } else if (isWeixinBrower) {
          return new Promise((resolve, reject) => {
            window.WeixinJSBridge.invoke('getBrandWCPayRequest', wapPayRequest, function (res) {
              if (res.err_msg !== 'get_brand_wcpay_request:ok') {
                reject(new Error(i18n.t('pay.payError.userCancel')))
              }
              resolve(undefined)
            })
          })
        } else {
          throw new Error(i18n.t('pay.payError.wechatOpen'))
          // throw new Error('Not in weixin mini program or browser.')
        }
      }
    } else if (!externalPayMethod) {
      if (this._weixinMiniToPay()) {
        this._asyncPayResult = async (result, resultOrdersId) => {
          const { batchPayParams, ordersId } = result
          let _url = `/pages/pay/index?type=platform&p=${encodeURIComponent(JSON.stringify(batchPayParams))}&o=${ordersId}`
          if (resultOrdersId) {
            _url += `&ro=${resultOrdersId}`
          }
          if (cartMobx.posOrderPay) {
            _url += '&scene=posOrderPay'
          }
          setCookie(`externalPayMethod.WechatPayWeb.${restaurantConfig.brandId}`, 1, Date.now() + 15000)
          if (Config.buyTogetherId) {
            setCookie(`buyTogetherId.${restaurantConfig.brandId}`, Config.buyTogetherId, Date.now() + 3 * 60 * 1000)
          }
          wx?.miniProgram?.redirectTo({
            url: _url
          })
          if (Config.buyTogetherId) {
            return 'async'
          }
        }
      }
    }
  }

  static async _prepareApplePayWeb (methodParam?): Promise<void> {
    const applePayWebMethod = methodParam?.key === 'applePay' ? methodParam : find(
      payPlanMobx.payMethods,
      (x) => x.type === 'ApplePayWeb'
    ) as IApplePayWebPayMethod
    if (applePayWebMethod) {
      const { channel } = applePayWebMethod
      if (channel === 'CardConnectApplePay' || channel === 'GlobalPaymentsApplePay') {
        let method: ICardConnectApplePayWebPayMethod | IGlobalPaymentsApplePayWebPayMethod
        if (channel === 'CardConnectApplePay') {
          method = applePayWebMethod as ICardConnectApplePayWebPayMethod
        } else if (channel === 'GlobalPaymentsApplePay') {
          method = applePayWebMethod as IGlobalPaymentsApplePayWebPayMethod
        }
        const { cardInfo } = method
        if (!cardInfo) {
          const total: ApplePayJS.ApplePayLineItem = {
            type: 'final',
            label: storeMobx.info.storeName,
            amount: String(method.amount),
          }
          return new Promise((resolve, reject) => {
            const session = new ApplePaySession(3, {
              countryCode: 'US',
              currencyCode: 'USD',
              supportedNetworks: ['amex', 'visa', 'masterCard', 'discover'],
              merchantCapabilities: ['supports3DS'],
              total,
            })
            session.onvalidatemerchant = async function (event) {
              console.log('onvalidatemerchant', event)
              try {
                const { result } = (await PayApi.applePayMerchantValidate({
                  orderSource: 'dt',
                  useType: 'dc',
                  storeId: storeMobx.info.storeId,
                  validationURL: event.validationURL,
                })) as any
                console.log('validate result', result)
                session.completeMerchantValidation(result)
              } catch (e) {
                session.abort()
                reject(e)
              }
            }
            session.onpaymentmethodselected = async function (event) {
              console.log('onpaymentmethodselected', event)
              session.completePaymentMethodSelection({ newTotal: total })
            }
            session.onpaymentauthorized = async function (event) {
              console.log('onpaymentauthorized', event)
              const { paymentData } = event.payment.token
              const { data, header, signature, version } = paymentData
              const { ephemeralPublicKey, publicKeyHash, transactionId } =
                header
              payPlanMobx.setMethodCardInfo({
                data,
                signature,
                version,
                ephemeralPublicKey,
                publicKeyHash,
                transactionId,
              })
              resolve()
            }
            session.oncancel = function () {
              console.log('oncancel')
              reject('cancel')
            }
            if (channel === 'CardConnectApplePay') {
              this._cardConnectApplePaySession = session
            } else if (channel === 'GlobalPaymentsApplePay') {
              this._globalPaymentsApplePaySession = session
            }
            session.begin()
          })
        }
      } else if (channel === 'StripeApplePay') {
        const method = applePayWebMethod as IStripeApplePayWebPayMethod
        const { cardInfo } = method
        if (!cardInfo) {
          return new Promise((resolve, reject) => {
            const Stripe = (window as any).Stripe
            Stripe.setPublishableKey(
              payChannelMobx.channelAccount.StripeApplePay?.publicApiKey
            )
            const paymentRequest = {
              countryCode: 'US',
              currencyCode: 'USD',
              total: {
                label: storeMobx.info.storeName,
                amount: String(method.amount),
              },
            }
            const session = Stripe.applePay.buildSession(
              paymentRequest,
              (result, completion) => {
                payPlanMobx.setMethodCardInfo({ auth_code: result.token.id })
                this._stripeApplePayCallback = completion
                resolve()
              },
              reject
            )
            session.oncancel = function () {
              console.log('oncancel')
              reject('cancel')
            }
            session.begin()
          })
        }
      }
    }
  }

  static async _saveBankCard (methodParam?): Promise<void> {
    const bankCardOnlineMethod = methodParam?.key === 'bankCardOnline' ? methodParam : find(
      payPlanMobx.payMethods,
      (x) => x.type === 'BankCardOnline'
    ) as IBankCardOnlinePayMethod
    if (bankCardOnlineMethod) {
      const { bankCardInfo } = bankCardOnlineMethod
      if (bankCardInfo.tokenType === 'multi') {
        bankCardMobx.setLastUsed(bankCardInfo)
      }
    }
  }

  /** 下单并发起支付后，真正执行支付 */
  static async doPay (prepayCreate: IPrepayCreate): Promise<void | 'async'> {
    try {
      this._saveBankCard()
      await this._lock(prepayCreate)
      const settelResult = await this._settle(prepayCreate)
      if (this._cardConnectApplePaySession) {
        this._cardConnectApplePaySession.completePayment(
          (window as any).ApplePaySession.STATUS_SUCCESS
        )
      }
      if (this._globalPaymentsApplePaySession) {
        this._globalPaymentsApplePaySession.completePayment(
          (window as any).ApplePaySession.STATUS_SUCCESS
        )
      }
      if (this._stripeApplePayCallback) {
        this._stripeApplePayCallback(
          (window as any).ApplePaySession.STATUS_SUCCESS
        )
      }
      if (this._payOpenData) {
        this._payOpenData = undefined
      }
      if (this._asyncPayResult) {
        setTimeout(() => {
          memberMobx.refreshMemeberBenefits()
        }, 1000)
        return await this._asyncPayResult(settelResult)
      }
    } catch (e) {
      if (this._cardConnectApplePaySession) {
        this._cardConnectApplePaySession.abort()
      }
      if (this._globalPaymentsApplePaySession) {
        this._globalPaymentsApplePaySession.abort()
      }
      if (this._stripeApplePayCallback) {
        this._stripeApplePayCallback(
          (window as any).ApplePaySession.STATUS_FAILURE
        )
      }
      if (this._asyncPayResult) {
        this._asyncPayResult = undefined
      }
      throw e
    }
  }

  static async _lock (prepayCreate: IPrepayCreate): Promise<void> {
    if (!prepayCreate.clientNumber) {
      const { error } = (await PayApi.lockPreCreate(
        prepayCreate.prepayCreateId,
        prepayCreate.ordersId,
        appMobx.deviceId,
        true
      )) as any
      if (error) {
        throw error
      }
    } else if (prepayCreate.clientNumber !== appMobx.deviceId) {
      throw new Error(i18n.t('pay.payingByOther'))
    }
  }

  static async _settle (prepayCreate: IPrepayCreate): Promise<void> {
    const {
      prepayCreateId,
      storeId,
      ordersId,
      clientNumber,
      amount,
      amountTax,
      amountService,
    } = prepayCreate

    // throw error
    if (toDecimal(amount, 2, true) !== toDecimal(payPlanMobx.orderAmount, 2, true)) {
      throw new Error(i18n.t('pay.payError.amountChange'))
      // throw new Error(
      //   `订单金额不一致，下单前：${payPlanMobx.orderAmount}，下单后：${amount}`
      // )
    }

    // return url
    function getReturnUrl (method: TPayMethod) {
      if (method.type === 'PayPalWeb') {
        return `${getHandTopUrl()}/paypalreturn/${ordersId}${location.search}`
      }
    }

    // 更新方案订单信息，避免失败后由于下单前订单信息与下单后订单信息不一致，导致方案从头重新计算
    // payPlanMobx.updateOrder({
    //   type: 'dining',
    //   uniqueKey: ordersId,
    //   subtotal: toDecimal(amount - amountTax - amountService, 2, true) as number,
    //   tax: amountTax,
    //   extraFee: amountService,
    // })
    // 支付渠道
    let channels: IBatchPayChannelParam[]
    if (payPlanMobx.totalAmount > 0) {
      channels = await Promise.all(
        map(payPlanMobx.payMethods, async (x) => {
          const ext = {} as Record<string, string | number>
          let isSurcharge = false
          if (x.type === 'MemberCard') {
            ext.cardId = x.memberCard.cardId
            ext.cardNumber = x.memberCard.cardNo
            ext.cartName = x.memberCard.card?.cardName
            ext.memberId = memberMobx.memberInfo.memberId
            ext.token = memberMobx.token
          } else if (x.type === 'GiftCard') {
            ext.cardId = x.giftCard.giftcardId
            ext.cardNumber = x.giftCard.cardNo
            ext.cartName = x.giftCard.giftcard.giftcardName
            ext.token = memberMobx.token
          } else if (x.type === 'BankCardOnline') {
            if (x.bankCardInfo.tokenType === 'single') {
              ext.authCode = x.bankCardInfo.token
            } else if (x.bankCardInfo.tokenType === 'multi') {
              ext.creditCardNo = x.bankCardInfo.creditCardNo
            }
            isSurcharge = x.bankCardInfo?.creditCardType !== 'N'
          } else if (x.type === 'ApplePayWeb') {
            if (x.cardInfo) {
              Object.assign(ext, x.cardInfo)
            }
          } else if (x.type === 'PayPalWeb') {
            ext.returnUrl = getReturnUrl(x)
          } else if (x.type === 'AlipayApp') {
            let buyerId = this._payOpenData?.openId
            if (isEmpty(buyerId)) {
              console.debug('buyerId', buyerId)
              PayApi.openIdErrorDingTalkSend(ordersId, this._payOpenData?.openId)
              throw new Error(i18n.t('pay.payError.alipayScan'))
            }
            Object.assign(ext, { buyerId })
          } else if (x.type === 'WechatPayWeb') {
            const { isWeixinMiniProgram, isWeixinBrower } = getPlatform()
            let appId, openId
            if (isWeixinMiniProgram) {
              appId = this._payOpenData?.appId
              openId = this._payOpenData?.openId
            } else if (isWeixinBrower) {
              openId = this._payOpenData?.openId
            }
            if (isEmpty(openId)) {
              console.debug('openId', openId)
              PayApi.openIdErrorDingTalkSend(ordersId, this._payOpenData?.openId)
              throw new Error(i18n.t(isWeixinMiniProgram ? 'pay.payError.wechatMini' : 'pay.payError.wechatScan'))
            }
            Object.assign(ext, { appId, openId })
          } else if (x.type === 'ThirdCouponPay') {
            ext.couponCode = x.channelExt.couponCode
            ext.couponAmount = x.channelExt.couponAmount
            ext.couponPayAmount = x.channelExt.couponPayAmount
            ext.couponPrepareCode = x.channelExt.couponPrepareCode
          }
          // 附加费
          const surchargeConfig = surchargeMobx.surchargeMap?.[x.channel]
          const surchargeInfo: any = {}
          if (isSurcharge && surchargeConfig) {
            const surcharge = toDecimal(x.amount * (surchargeConfig.rate / 100), 2)
            if (surcharge) {
              surchargeInfo.surcharge = surcharge
              surchargeInfo.paymentSurchargeConfigUuid = surchargeConfig.id
              surchargeInfo.surchargeName = surchargeConfig.name
              surchargeInfo.surchargeRate = surchargeConfig.rate
            }
          }
          return {
            requestId: uuid(),
            channelType: x.channel,
            amount: toDecimal(x.amount, 2),
            tip: toDecimal(x.tip, 2),
            ext,
            ...surchargeInfo,
          }
        })
      )
      channels = filter(channels, (x) => Number(x.amount) + Number(x.tip) > 0)
    } else {
      channels = [{
        requestId: uuid(),
        channelType: 'ZeroPay',
        amount: 0,
        tip: 0,
        ext: {}
      }]
    }

    // batchPay 参数
    const currency = channels?.some(c => c.channelType === 'GlobalPaymentsBankCard') ? 'CAD' : 'USD'
    const batchPayParams = {
      bo,
      prepayCreateId,
      clientNumber,
      orderSource: 'dt',
      useType: 'dc',
      storeId,
      currency,
      // 支付宝支付时，需要传入appId
      opAppId: Config.appId || '',
      requestId: uuid(),
      ordersId,
      channels,
      ext: {
        deviceType: 'mobile',
        deviceId: appMobx.deviceId,
      },
    }
    // todo 测试
    console.log(batchPayParams, 'batchPayParams')
    // return
    // GiftCard | MemberCard | ZeroPay 拦截去小程序 batchPay
    if (this._weixinMiniToPay() && channels?.length && channels.every(item => ['GiftCard', 'MemberCard', 'MemberBalance', 'ZeroPay', 'ThirdCouponPay', 'DouyinCoupon'].includes(item.channelType))) {
      return { batchPayParams, ordersId } as any
    }

    const { error, result } = (await PayApi.batchPay(batchPayParams)) as any

    if (error || result.error) {
      throw error || result
    }
    payPlanMobx.clear()
    return result
  }
  static async resume (
    orderId: string,
    isForce?: boolean,
  ): Promise<'CANCEL' | 'SUCCESS' | 'PAYING'> {
    if (!orderId) {
      return
      // throw new Error('orderId is required to resume.')
    }
    const { result, error } = (await PayApi.preByOrdersId(orderId)) as any
    if (error) {
      throw error
    }
    const prepayCreate = result as IPrepayCreate
    if (!prepayCreate) {
      // 不需要报错
      return
      // throw new Error(`Cannot find prepay create for ${orderId}`)
    }
    const { prepayCreateId, clientNumber, status } = prepayCreate
    if (status === 'Success') {
      return 'SUCCESS'
    } else if (status === 'Closed') {
      return 'CANCEL'
    } else {
      // 三方券支付失败延迟200ms调用
      await delay(200)
      const { error } = (await PayApi.cancelPay(
        prepayCreateId,
        isForce ? clientNumber || appMobx.deviceId : appMobx.deviceId || clientNumber,
        orderId
      )) as any
      if (error) {
        // TODO: 付到一半没办法从手机端取消
        throw error
      }
      return 'CANCEL'
    }
  }

  static holdAsyncOrder (ordersId) {
    const payStore = store.namespace('pay')
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    payStore.set('asyncPayOrdersId.' + ordersId, 'async')
  }
  static clearHoldAsync (ordersId) {
    const payStore = store.namespace('pay')
    payStore.remove('asyncPayOrdersId.' + ordersId)
  }
  static isOrderAsync (ordersId) {
    const payStore = store.namespace('pay')
    const value = payStore.get('asyncPayOrdersId.' + ordersId)
    return value === 'async'
  }

  static async preOrdersBatchPay (method: any): Promise<any> {
    try {
      await this._checkOpenId(method)
      await this._prepareBankCardOnline(method, true)
      await this._prepareApplePayWeb(method)
      await this._prepareAsyncPay(method)
      return method
    } catch (e) {
      reject(e)
    }
  }

  static async ordersBatchPay (method: any, orders: any[]) {
    if (method?.key === 'bankCardOnline') {
      this._saveBankCard(method)
    }
    const orderArr = orders.map(order => {
      const saveOrder = {
        ...order,
        channels: [...order.channels.map(c => {
          return { ...c, requestId: uuid() }
        })],
        ext: {
          deviceType: 'mobile',
          deviceId: restaurantConfig.deviceId,
        },
        clientNumber: restaurantConfig.deviceId,
        bo
      }
      return saveOrder
    })
    const { error, result } = (await OrderAPI.batchOrdersPay(orderArr))
    if (error) {
      if (this._cardConnectApplePaySession) {
        this._cardConnectApplePaySession.abort()
      }
      if (this._globalPaymentsApplePaySession) {
        this._globalPaymentsApplePaySession.abort()
      }
      if (this._stripeApplePayCallback) {
        this._stripeApplePayCallback(
          (window as any).ApplePaySession.STATUS_FAILURE
        )
      }
      if (this._asyncPayResult) {
        this._asyncPayResult = undefined
      }
      throw error
    }
    payPlanMobx.clear()
    if (this._cardConnectApplePaySession) {
      this._cardConnectApplePaySession.completePayment(
        (window as any).ApplePaySession.STATUS_SUCCESS
      )
    }
    if (this._globalPaymentsApplePaySession) {
      this._globalPaymentsApplePaySession.completePayment(
        (window as any).ApplePaySession.STATUS_SUCCESS
      )
    }
    if (this._stripeApplePayCallback) {
      this._stripeApplePayCallback(
        (window as any).ApplePaySession.STATUS_SUCCESS
      )
    }
    if (this._payOpenData) {
      this._payOpenData = undefined
    }
    if (this._asyncPayResult) {
      setTimeout(() => {
        memberMobx.refreshMemeberBenefits()
      }, 1000)
      return await this._asyncPayResult(result, orderArr[1]?.ordersId)
    }
    return result
  }

  static getTempIsWxPay () {
    return getCookie(`externalPayMethod.WechatPayWeb.${restaurantConfig.brandId}`)
  }
}

export default CheckoutPayController
